import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { HeicConverterService } from "src/app/shared/services/heic-converter.service";
import { TravelKitService } from "src/app/shared/services/travel-kit.service";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import AppShareHelper from "src/app/shared/utils/helper";
import Swal from "sweetalert2";

export interface WallpaperModel {
  id?: number;
  name: string;
  url: string;
  digitalMemoryLogId: number;
  updatedAt?: string;
}

@Component({
  selector: "app-tab-wallpaper",
  templateUrl: "./tab-wallpaper.component.html",
  styleUrls: ["./tab-wallpaper.component.scss"],
})
export class TabWallpaperComponent implements OnInit, OnChanges {
  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};

  // Wallpaper data from parent component
  @Input() wallpapers: any[] = [];
  @Input() wallpaperFiles: any[] = [];
  @Input() deleteWallpapers: any[] = [];
  @Input() wallpaperChanges: boolean[] = [];
  @Input() originalWallpapers: any[] = [];

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();
  @Output() wallpapersChange = new EventEmitter<any[]>();
  @Output() wallpaperFilesChange = new EventEmitter<any[]>();
  @Output() deleteWallpapersChange = new EventEmitter<any[]>();
  @Output() wallpaperChangesChange = new EventEmitter<boolean[]>();
  @Output() originalWallpapersChange = new EventEmitter<any[]>();

  // Remove internal wallpaper management (now managed by parent)
  // wallpapers: any[] = [];
  // wallpaperFiles: any[] = [];
  // deleteWallpapers: any[] = [];
  // wallpaperChanges: boolean[] = [];
  // originalWallpapers: any[] = [];

  animalPOIFile: File;
  private initializedFromModel = false;

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private travelKitService: TravelKitService,
    private heicConverter: HeicConverterService
  ) {}

  ngOnInit() {
    this.initializeWallpaperData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["model"] && changes["model"].currentValue && !this.initializedFromModel) {
      this.initializeWallpaperData();
    }
  }

  private initializeWallpaperData() {
    const hasParentState = Array.isArray(this.wallpaperFiles) && Array.isArray(this.wallpapers);
    if (hasParentState) {
      this.wallpaperChanges = Array.isArray(this.wallpaperChanges)
        ? this.wallpaperChanges
        : new Array(this.wallpapers.length).fill(false);
      this.originalWallpapers = Array.isArray(this.originalWallpapers)
        ? this.originalWallpapers
        : AppShareHelper.deepClone(this.wallpapers);
      this.initializedFromModel = true;
      return;
    }

    const newWallpapers = Array.isArray(this.model.wallpapers) ? this.model.wallpapers : [];
    const newWallpaperFiles = newWallpapers.map(wallpaper => ({
      file: null,
      src: wallpaper.url,
      title: wallpaper.name,
      time: new Date(wallpaper.updatedAt || Date.now()),
    }));

    this.wallpapersChange.emit(newWallpapers);
    this.wallpaperFilesChange.emit(newWallpaperFiles);
    this.wallpaperChangesChange.emit(new Array(newWallpapers.length).fill(false));
    this.originalWallpapersChange.emit(AppShareHelper.deepClone(newWallpapers));
    this.initializedFromModel = true;

    console.log(this.wallpaperFiles);
  }
  async saveWallpapers(): Promise<boolean> {
    try {
      // Handle deletions
      if (this.deleteWallpapers.length > 0) {
        for (const wallpaperId of this.deleteWallpapers) {
          await this.travelKitService.deleteWallpaper(wallpaperId).toPromise();
        }
      }

      // Update model with current wallpapers
      this.model.wallpapers = this.wallpapers;
      this.modelChange.emit(this.model);

      // Reset tracking and emit to parent
      this.deleteWallpapersChange.emit([]);
      this.wallpaperChangesChange.emit(new Array(this.wallpapers.length).fill(false));
      this.originalWallpapersChange.emit(AppShareHelper.deepClone(this.wallpapers));

      return true;
    } catch (error) {
      console.error("Error saving wallpapers:", error);
      this.toastr.error("Failed to save wallpaper changes");
      return false;
    }
  }

  validateWallpapers(): boolean {
    if (this.model.displayWallpapers && (!this.wallpapers || this.wallpapers.length === 0)) {
      this.toastr.error("Please complete all required fields for wallpaper");
      return false;
    }

    return true;
  }

  async onWallpaperFilesSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    // Ensure arrays are properly initialized
    const currentWallpapers = Array.isArray(this.wallpapers) ? this.wallpapers : [];
    const currentWallpaperFiles = Array.isArray(this.wallpaperFiles) ? this.wallpaperFiles : [];

    const currentWallpaperCount = currentWallpapers.length;
    const uploadingCount = currentWallpaperFiles.filter(wf => wf.uploadKey || wf.tempId).length;
    const totalCurrentCount = currentWallpaperCount + uploadingCount;

    if (totalCurrentCount + files.length > 10) {
      this.toastr.error(
        `You can only upload up to 10 wallpapers. Current: ${totalCurrentCount}, trying to add: ${files.length}`
      );
      return;
    }

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!this.isFileTypeValid(file)) {
        this.toastr.error(`File ${file.name} format not supported. Accepted formats: JPEG, JPG, PNG`);
        continue;
      }

      if (!this.isFileSizeValid(file)) {
        this.toastr.error(`File ${file.name} exceeds 20MB limit`);
        continue;
      }

      validFiles.push(file);
    }

    for (let index = 0; index < validFiles.length; index++) {
      let file = validFiles[index];

      try {
        file = await this.heicConverter.convertToJpegIfNeeded(file);

        const timestamp = Date.now();
        const uploadKey = this.generateUploadKey("wallpaper", file.name, `${timestamp}_${index.toString()}`);
        const tempId = `temp_${timestamp}_${index}_${Math.random().toString(36).substr(2, 9)}`;

        this.setUploadState(uploadKey, true);

        const wallpaperItem = {
          file: file,
          src: null,
          title: file.name,
          time: new Date(),
          uploadKey: uploadKey,
          tempId: tempId,
        };

        const reader = new FileReader();
        reader.onload = (e: any) => {
          wallpaperItem.src = e.target.result;
        };
        reader.readAsDataURL(file);

        const updatedWallpaperFiles = Array.isArray(this.wallpaperFiles)
          ? [...this.wallpaperFiles, wallpaperItem]
          : [wallpaperItem];
        this.wallpaperFilesChange.emit(updatedWallpaperFiles);

        await new Promise<void>((resolve, reject) => {
          this.uploadService.uploadFileToS3("digital/photos", file).subscribe(
            (response: any) => {
              if (response && response.type === "progress") {
                this.setUploadProgress(uploadKey, response.percent);
                return;
              }

              if (response && response.type === "complete") {
                this.setUploadState(uploadKey, false);

                const wallpaperIndex = this.wallpaperFiles.findIndex(wp => wp.tempId === wallpaperItem.tempId);

                if (wallpaperIndex !== -1) {
                  const updatedWallpaperFiles = [...this.wallpaperFiles];
                  updatedWallpaperFiles[wallpaperIndex].src = response.url;
                  updatedWallpaperFiles[wallpaperIndex].uploadKey = null;
                  delete updatedWallpaperFiles[wallpaperIndex].tempId;
                  this.wallpaperFilesChange.emit(updatedWallpaperFiles);
                }

                const wallpaper = {
                  name: file.name,
                  url: response.url,
                  digitalMemoryLogId: this.model.id,
                };

                this.travelKitService
                  .createWallpaper(wallpaper)
                  .toPromise()
                  .then((createResponse: any) => {
                    if (createResponse && createResponse.Data) {
                      const updatedWallpapers = [...this.wallpapers, createResponse.Data];
                      this.wallpapersChange.emit(updatedWallpapers);
                    }
                    this.toastr.success(`Wallpaper ${file.name} uploaded successfully`);
                    this.cleanupUpload(uploadKey);
                    resolve();
                  })
                  .catch(error => {
                    this.toastr.error("Failed to save wallpaper to database");
                    this.cleanupUpload(uploadKey);
                    resolve();
                  });
              }
            },
            error => {
              this.setUploadState(uploadKey, false);

              const itemIndex = this.wallpaperFiles.findIndex(wp => wp.tempId === wallpaperItem.tempId);
              if (itemIndex > -1) {
                const updatedWallpaperFiles = [...this.wallpaperFiles];
                updatedWallpaperFiles.splice(itemIndex, 1);
                this.wallpaperFilesChange.emit(updatedWallpaperFiles);
              }

              this.toastr.error(`Failed to upload ${file.name}: ${error.message || error}`);
              console.error("Upload error:", error);
              this.cleanupUpload(uploadKey);
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error(`Failed to upload wallpaper ${file.name}:`, error);
      }
    }
  }

  deleteWallpaper(wallpaper: any, index: number) {
    const wallpaperData = this.wallpapers[index];

    if (wallpaperData && wallpaperData.id) {
      const updatedDeleteWallpapers = [...this.deleteWallpapers, wallpaperData.id];
      this.deleteWallpapersChange.emit(updatedDeleteWallpapers);

      const updatedWallpaperFiles = [...this.wallpaperFiles];
      const updatedWallpapers = [...this.wallpapers];

      updatedWallpaperFiles.splice(index, 1);
      updatedWallpapers.splice(index, 1);

      this.wallpaperFilesChange.emit(updatedWallpaperFiles);
      this.wallpapersChange.emit(updatedWallpapers);
      this.model.wallpapers = updatedWallpapers;
      this.modelChange.emit(this.model);
      this.toastr.success("Wallpaper marked for deletion");
    } else {
      const updatedWallpaperFiles = [...this.wallpaperFiles];
      const updatedWallpapers = [...this.wallpapers];

      updatedWallpaperFiles.splice(index, 1);
      if (updatedWallpapers[index]) {
        updatedWallpapers.splice(index, 1);
      }

      this.wallpaperFilesChange.emit(updatedWallpaperFiles);
      this.wallpapersChange.emit(updatedWallpapers);
      this.model.wallpapers = updatedWallpapers;
      this.modelChange.emit(this.model);
      this.toastr.success("Wallpaper removed");
    }
  }

  deleteAllWallpapers() {
    if (!this.wallpaperFiles || this.wallpaperFiles.length === 0) {
      this.toastr.info("No wallpapers to delete");
      return;
    }

    const wallpaperCount = this.wallpaperFiles.length;

    Swal.fire({
      title: "Are you sure?",
      text: `This will delete all ${wallpaperCount} wallpaper(s).`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const wallpaperIdsToDelete = this.wallpapers
          .filter(wallpaper => wallpaper.id && typeof wallpaper.id === "number" && wallpaper.id > 0)
          .map(wallpaper => wallpaper.id);

        if (wallpaperIdsToDelete.length > 0) {
          const updatedDeleteWallpapers = [...this.deleteWallpapers, ...wallpaperIdsToDelete];
          this.deleteWallpapersChange.emit(updatedDeleteWallpapers);
        }

        this.wallpaperFilesChange.emit([]);
        this.wallpapersChange.emit([]);
        this.model.wallpapers = [];
        this.modelChange.emit(this.model);
        this.toastr.success(`All ${wallpaperCount} wallpaper(s) deleted successfully`);
      }
    });
  }

  // Helper methods
  generateUploadKey(type: string, fileName: string, identifier: string): string {
    return `${type}_${identifier}_${fileName}_${Date.now()}`;
  }

  setUploadState(key: string, uploading: boolean): void {
    this.uploadStates[key] = uploading;
  }

  setUploadProgress(key: string, progress: number): void {
    this.uploadProgress[key] = progress;
  }

  isUploadInProgress(key: string): boolean {
    return this.uploadStates[key] || false;
  }

  getProgressByKey(key: string): number {
    return this.uploadProgress[key] || 0;
  }

  cleanupUpload(key: string): void {
    delete this.uploadStates[key];
    delete this.uploadProgress[key];
  }

  isFileSizeValid(file: File): boolean {
    const maxSize = 20 * 1024 * 1024; // 20MB
    return file.size <= maxSize;
  }

  isFileTypeValid(file: File): boolean {
    const fileTypes = ["image/jpeg", "image/jpg", "image/png", "image/heic", "image/heif"];
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    return (
      fileTypes.includes(file.type) || (isHeicByExtension && (!file.type || file.type === "application/octet-stream"))
    );
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  onModelChange() {
    this.modelChange.emit(this.model);
  }

  autoCheck(field: string) {
    this.autoCheckEvent.emit(field);
  }

  getVideoUploadText(subsection: any): string {
    if (!subsection.media || subsection.media.length === 0) {
      return "Select Videos";
    }
    const count = subsection.media.length;
    return count === 1 ? "1 video selected" : `${count} videos selected`;
  }

  getFileUploadText(): string {
    const totalWallpapers = this.wallpapers.length || this.wallpaperFiles.length || 0;

    if (totalWallpapers === 0) {
      return "Select File";
    }

    if (totalWallpapers === 1) {
      return "1 wallpaper selected";
    }

    return `${totalWallpapers} wallpapers selected`;
  }
}
